import { format } from '@seek/currency';
import { Section, useConfig } from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';
import {
  ButtonIcon,
  Card,
  Column,
  Columns,
  IconThumb,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';
import { useParams } from 'react-router';
import type { SalariesTabModel } from '../../../pages/salaries/salariesTabModel';
import translations from './.vocab/index';
import { SalaryRangeInfo } from './SalaryRangeInfo';
interface SalariesByRoleSectionProps {
  model: Pick<SalariesTabModel, 'companyName' | 'salaryRanges'>;
}

export const SalariesByRoleSection = ({
  model,
}: SalariesByRoleSectionProps) => {
  const { t } = useTranslations(translations);
  const { locale } = useConfig();
  const params = useParams();
  const roleId = params.roleId;

  const { companyName, salaryRanges } = model;

  if (!salaryRanges) {
    return null;
  }

  const { currency, lastUpdated, salaryType } = salaryRanges;
  const roleSalaryRange = salaryRanges.salaryRange?.find(
    (range) => range.roleId === roleId,
  );

  if (!roleSalaryRange) {
    return null;
  }

  // Phase 1 focus only on company salary range
  const { roleTitle, rangeByCompany } = roleSalaryRange;

  if (!rangeByCompany) {
    return null;
  }

  const { jobCount, maxSalary, minSalary } = rangeByCompany;

  const formattedMinSalary = format(minSalary, {
    notation: 'compact',
    currency: currency,
    locale,
  });
  const formattedMaxSalary = format(maxSalary, {
    notation: 'compact',
    currency: currency,
    locale,
  });

  const salaryRange = `${formattedMinSalary} - ${formattedMaxSalary}`;

  // TODO: To be updated after we have correct data return from graph
  const salaryPeriod = salaryType === 'Annual' ? 'per year' : 'per month';
  const description = t('The average salary based on past job ads');
  const tooltipText = t('This estimate is based on...', {
    companyName,
    jobCount: jobCount?.toString(),
  });
  return (
    <Section
      mode={'view'}
      heading={t('role salary at companyName', {
        companyName,
        role: roleTitle,
      })}
      padding={'none'}
    >
      <Stack space="small">
        <Card tone="formAccent">
          <SalaryRangeInfo
            salaryRange={salaryRange}
            salaryPeriod={salaryPeriod}
            description={description}
            tooltipText={tooltipText}
          />
        </Card>
        <Columns space={'none'}>
          <Column>
            <Inline space="xsmall" alignY="center">
              <Text tone="secondary" size="xsmall">
                {t('Was this helpful?')}
              </Text>
              <ButtonIcon
                variant="transparent"
                size="small"
                icon={<IconThumb />}
                label={t('Yes')}
              />
              <ButtonIcon
                variant="transparent"
                size="small"
                icon={<IconThumb direction="down" />}
                label={t('No')}
              />
            </Inline>
          </Column>
          <Column>
            <Text size="xsmall" align="right" tone="secondary">
              {t('Refreshed date', { date: lastUpdated })}
            </Text>
          </Column>
        </Columns>
      </Stack>
    </Section>
  );
};
